import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import {
  PiFileTextLight,
  PiBriefcaseLight,
  PiFileDocLight,
  PiHandshakeLight,
  PiNoteLight,
  PiNotebookLight,
  PiStampLight,
  PiScrollLight,
  PiShieldCheckLight,
  PiKeyLight,
  PiBellLight,
  PiBuildingLight,
  PiLightbulbLight,
  PiCheckCircleLight,
  PiCaretDownLight,
  PiClockAfternoonLight,
  PiBooksLight,
} from 'react-icons/pi';
import { useHistory, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { formatInsightName } from '../../utils/functions';

export interface TabItem {
  label: string;
  renderedLabel?: React.ReactNode;
  value: string;
  icon?: React.ReactNode;
  tooltip?: string;
  disabled?: boolean;
  position?: 'left' | 'right';
}

interface MatterTabBarProps {
  tabs: TabItem[];
  activeTab: string;
  onTabClick: (tabValue: string) => void;
  className?: string;
  showLabels?: boolean;
  allDraftTypes: any[];
  selectedDraftTypeIds: number[];
  allInsightTypes: any[]; 
  selectedInsightTypeIds: number[];
}

type IconComponent = React.ComponentType<{ className?: string }>;

const topLevelIcons: { [key: string]: JSX.Element } = {
  'Contracts': <PiBriefcaseLight className="h-4 w-4 text-gray-500" />,
  'Pleadings': <PiFileDocLight className="h-4 w-4 text-gray-500" />,
  'Motions': <PiHandshakeLight className="h-4 w-4 text-gray-500" />,
  'Legal Memoranda': <PiNoteLight className="h-4 w-4 text-gray-500" />,
  'Briefs': <PiNotebookLight className="h-4 w-4 text-gray-500" />,
  'Plea Agreements': <PiStampLight className="h-4 w-4 text-gray-500" />,
  'Petitions': <PiScrollLight className="h-4 w-4 text-gray-500" />,
  'Affidavits': <PiFileTextLight className="h-4 w-4 text-gray-500" />,
  'Declarations': <PiFileTextLight className="h-4 w-4 text-gray-500" />,
  'Discovery Requests': <PiFileTextLight className="h-4 w-4 text-gray-500" />,
  'Trademark Applications': <PiFileTextLight className="h-4 w-4 text-gray-500" />,
  'Wills': <PiShieldCheckLight className="h-4 w-4 text-gray-500" />,
  'Trusts': <PiShieldCheckLight className="h-4 w-4 text-gray-500" />,
  'Powers of Attorney': <PiKeyLight className="h-4 w-4 text-gray-500" />,
  'Demand Letters': <PiScrollLight className="h-4 w-4 text-gray-500" />,
  'Settlement Agreements': <PiHandshakeLight className="h-4 w-4 text-gray-500" />,
  'Notices': <PiBellLight className="h-4 w-4 text-gray-500" />,
  'Corporate Resolutions': <PiBuildingLight className="h-4 w-4 text-gray-500" />,
  'Patent Applications': <PiLightbulbLight className="h-4 w-4 text-gray-500" />,
  'Compliance Reports': <PiCheckCircleLight className="h-4 w-4 text-gray-500" />,
  'Chronology': <PiClockAfternoonLight className="h-4 w-4 text-gray-500" />,
  'case_analysis': <PiFileTextLight className="h-4 w-4 text-gray-500" />,
  'strategies': <PiBriefcaseLight className="h-4 w-4 text-gray-500" />,
  'advocacy': <PiFileDocLight className="h-4 w-4 text-gray-500" />,
  'simulations': <PiNotebookLight className="h-4 w-4 text-gray-500" />,
  'research': <PiBooksLight className="h-4 w-4 text-gray-500" />,
  'compliance': <PiShieldCheckLight className="h-4 w-4 text-gray-500" />,
};

const renderIcon = (icon: React.ReactNode, className?: string): React.ReactNode => {
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon as React.ReactElement<any>, { className });
  }
  if (typeof icon === 'function') {
    const IconComp = icon as IconComponent;
    return <IconComp className={className} />;
  }
  return icon;
};

const MatterTabBar: React.FC<MatterTabBarProps> = ({
  tabs,
  activeTab,
  onTabClick,
  className,
  showLabels = true,
  allDraftTypes,
  selectedDraftTypeIds,
  allInsightTypes,
  selectedInsightTypeIds,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [showDropdown, setShowDropdown] = useState<string | null>(null); 
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({
    Documents: null,
    Insights: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const { matterId, draftId, insightId } = useParams<{ matterId: string; draftId?: string; insightId?: string }>();
  const draftTypeHierarchy = useMemo(() => {
    const activeDrafts = allDraftTypes.filter((dt: any) => dt.IsActive);
    const topLevel = activeDrafts
      .filter((dt: any) => dt.ParentDraftID !== null)
      .map((dt: any) => ({
        ...dt,
        children: activeDrafts
          .filter((child: any) => child.ParentDraftID === dt.DraftID)
          .map((child: any) => ({ ...child, children: [] })),
      }));
    const noParent = activeDrafts
      .filter((dt: any) => !dt.ParentDraftID && !topLevel.some((t: any) => t.DraftID === dt.DraftID))
      .map((dt: any) => ({ ...dt, children: [] }));
    return [...topLevel, ...noParent];
  }, [allDraftTypes, selectedDraftTypeIds]);

  const insightTypeHierarchy = useMemo(() => {
    const activeInsights = allInsightTypes.filter((it: any) => it.IsActive);
    const topLevel = activeInsights
      .filter((it: any) => it.ParentInsightID !== null)
      .map((it: any) => ({
        ...it,
        children: activeInsights
          .filter((child: any) => child.ParentInsightID === it.InsightID)
          .map((child: any) => ({ ...child, children: [] })),
      }));
    const noParent = activeInsights
      .filter((it: any) => !it.ParentInsightID && !topLevel.some((t: any) => t.InsightID === it.InsightID))
      .map((it: any) => ({ ...it, children: [] }));
    return [...topLevel, ...noParent];
  }, [allInsightTypes, selectedInsightTypeIds]);

  const handleDraftClick = (draftId: number) => {
    onTabClick('Documents');
    history.push(`/matters/${matterId}/document/${draftId}?tab=Documents`);
  };

  const handleInsightClick = (insightId: number) => {
    onTabClick('Insights');
    history.push(`/matters/${matterId}/insight/${insightId}?tab=Insights`);
  };

  const handleMouseEnter = (tabValue: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowDropdown(tabValue);
    const buttonRef = buttonRefs.current[tabValue];
    if (buttonRef) {
      const rect = buttonRef.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - 320, 
        width: 320,
      });
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowDropdown(null);
      setDropdownPosition(null);
    }, 1000);
  };

  const handleDropdownMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowDropdown(null);
      setDropdownPosition(null);
    }, 500);
  };

  const renderTab = (tab: TabItem, index: number, isRightAligned = false) => {
    const isActive = activeTab === tab.value;
    const tabClasses = clsx(
      'flex items-center px-3 py-1 text-xs font-medium rounded transition-all duration-200 border border-1 hover:border-gray-300 hover:bg-white dark:hover:bg-black',
      isActive
        ? 'text-primary-600 dark:text-primary-300 bg-white dark:bg-black border-gray-300'
        : 'text-gray-600 dark:text-gray-300 border-transparent hover:border-gray-300 dark:hover:bg-gray-700',
      tab.disabled && 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700'
    );

    const iconClassName = clsx(
      'h-4 w-4 flex-shrink-0 transition-colors duration-200',
      isActive ? 'text-primary-600 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'
    );

    const renderDropdown = (tabValue: string, hierarchy: any[], idKey: string, nameKey: string, onClick: (id: number) => void, activeId?: string) => (
      showDropdown === tabValue && dropdownPosition && createPortal(
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
          className="max-h-[60vh] rounded-xl bg-white dark:bg-gray-800 shadow-xl ring-1 ring-black ring-opacity-5 overflow-y-auto scrollbar-thin"
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          <div className="py-2 px-2 min-h-[100px]">
            <div className="px-4 py-2 text-sm text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              {tab.renderedLabel ?? t(tab.label)}
            </div>
            {hierarchy.length === 0 ? (
              <div className="flex justify-center items-center py-4">
                {(tabValue === 'Documents' && allDraftTypes.length === 0) || tabValue === 'Insights' && allInsightTypes.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <svg className="animate-spin h-5 w-5 text-gray-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    {t(`Loading ${tabValue.toLowerCase()}...`)}
                  </div>
                ) : (
                  <p className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 rounded-md">
                    {t(`No ${tabValue.toLowerCase()} available`)}
                  </p>
                )}
              </div>
            ) : (
              hierarchy.map((item: any) => (
                <div key={item[idKey]} className="relative">
                  <button
                    onClick={() => onClick(item[idKey])}
                    className={clsx(
                      'w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2',
                      activeTab === tabValue && activeId === item[idKey].toString()
                        ? 'bg-gray-100 dark:bg-gray-700'
                        : ''
                    )}
                  >
                    {topLevelIcons[tabValue === 'Documents' ? item.Name : item.Name] || <PiFileTextLight className="h-4 w-4 text-gray-500" />}
                    <span className="truncate">{t(tabValue === 'Documents' ? item.Name : formatInsightName(item.Name))}</span>
                    {item.isActive && <PiCheckCircleLight className="ml-2 h-4 w-4 text-green-500" />}
                    {item.children.length > 0 && (
                      <PiCaretDownLight className="ml-auto h-4 w-4 text-gray-500" />
                    )}
                  </button>
                  {item.children.length > 0 && (
                    <div className="ml-6 border-l-2 border-gray-200 dark:border-gray-700">
                      {item.children.map((child: any) => (
                        <button
                          key={child[idKey]}
                          onClick={() => onClick(child[idKey])}
                          className={clsx(
                            'w-full text-left px-4 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2',
                            activeTab === tabValue && activeId === child[idKey].toString()
                              ? 'bg-gray-100 dark:bg-gray-700'
                              : ''
                          )}
                        >
                          <PiFileTextLight className="h-4 w-4 text-gray-500" />
                          <span className="truncate">{t(tabValue === 'Documents' ? child.Name : formatInsightName(child.Name))}</span>
                          {child.isActive && <PiCheckCircleLight className="ml-2 h-4 w-4 text-green-500" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </motion.div>,
        document.body
      )
    );

    // if (tab.value === 'Documents' || tab.value === 'Insights') {
    //   return (
    //     <div key={tab.value} className="relative flex-shrink-0">
    //       <button
    //         ref={(el) => (buttonRefs.current[tab.value] = el)}
    //         className={tabClasses}
    //         onClick={() => {
    //           onTabClick(tab.value);
    //         }}
    //         onMouseEnter={() => handleMouseEnter(tab.value)}
    //         onMouseLeave={handleMouseLeave}
    //         aria-label={t(tab.label)}
    //         aria-pressed={isActive}
    //       >
    //         <span className="flex items-center">
    //           {tab.icon && (
    //             <span className="mr-2 flex-shrink-0">
    //               {renderIcon(tab.icon, iconClassName)}
    //             </span>
    //           )}
    //           {showLabels && (
    //             <span className="whitespace-nowrap transition-colors duration-200">
    //               {t(tab.label)}
    //             </span>
    //           )}
    //         </span>
    //       </button>
    //       {renderDropdown(
    //         tab.value,
    //         tab.value === 'Documents' ? draftTypeHierarchy : insightTypeHierarchy,
    //         tab.value === 'Documents' ? 'DraftID' : 'InsightID',
    //         'Name',
    //         tab.value === 'Documents' ? handleDraftClick : handleInsightClick,
    //         tab.value === 'Documents' ? draftId : insightId
    //       )}
    //     </div>
    //   );
    // }

    return (
      <Tooltip key={tab.value} title={tab.tooltip || t(tab.label)} placement="top" arrow>
        <button
          onClick={() => {
            !tab.disabled && onTabClick(tab.value);
          }}
          disabled={tab.disabled}
          className={tabClasses}
          aria-label={t(tab.label)}
          aria-pressed={isActive}
        >
          <span className="flex items-center">
            {tab.icon && (
              <span className="mr-2 flex-shrink-0">
                {renderIcon(tab.icon, iconClassName)}
              </span>
            )}
            {showLabels && (
              <span className="whitespace-nowrap transition-colors duration-200">
                {tab.renderedLabel ?? t(tab.label)}
              </span>
            )}
          </span>
        </button>
      </Tooltip>
    );
  };

  return (
    <div className={clsx('relative flex items-center border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50', className)}>
      <div
        ref={tabsRef}
        className="flex-1 flex overflow-x-auto scrollbar-hide px-2 pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
        <div data-tour-id="matter-tabs-left" className="flex space-x-1 min-w-max flex-shrink-0">
          {tabs
            .filter(tab => tab.position === 'left' || !tab.position)
            .map((tab, index) => renderTab(tab, index, false))}
        </div>
        <div className="mx-4 flex-shrink-0">
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
        </div>
        <div data-tour-id="matter-tabs-right" className="flex space-x-1 min-w-max flex-shrink-0 ml-auto">
          {tabs
            .filter(tab => tab.position === 'right')
            .map((tab, index) => renderTab(tab, index, true))}
        </div>
      </div>
    </div>
  );
};

export default MatterTabBar;
